import React, { useEffect, useState } from 'react';
import {
  Form, Input, InputNumber, Select, Upload, Checkbox, Row, Col, message,
} from 'antd';
import { DollarOutlined } from '@ant-design/icons';
import { uploadImage } from './dealsApi';

const { TextArea } = Input;

const CONDITION_OPTIONS = [
  { value: 'new', label: 'Brand New' },
  { value: 'refurbished', label: 'Refurbished' },
  { value: 'used_open_box', label: 'Open Box' },
  { value: 'used', label: 'Used' },
];

const CURRENCY_OPTIONS = [
  { value: 'USD', label: 'USD — US Dollar' },
  { value: 'EUR', label: 'EUR — Euro' },
  { value: 'GBP', label: 'GBP — British Pound' },
  { value: 'CAD', label: 'CAD — Canadian Dollar' },
];

function indentCategoryName(name, depth) {
  if (!depth) return name;
  return `${'— '.repeat(depth)}${name}`;
}

/**
 * Shared deal fields for submit + edit (consumer and admin).
 * Expects Ant Form instance via `form`. Callers own submit buttons.
 */
export default function DealForm({
  form,
  merchants = [],
  categories = [],
  initialImageUrl = null,
  onImageChange,
  compact = false,
}) {
  const [imageUrl, setImageUrl] = useState(initialImageUrl);

  useEffect(() => {
    setImageUrl(initialImageUrl || null);
  }, [initialImageUrl]);

  const setImage = (url) => {
    setImageUrl(url);
    onImageChange?.(url);
  };

  const merchantOptions = merchants.map((m) => ({
    value: m.id,
    label: m.name,
  }));

  const categoryOptions = categories.map((c) => ({
    value: c.id,
    label: indentCategoryName(c.name, c.depth),
  }));

  return (
    <Form form={form} layout="vertical" scrollToFirstError>
      <Form.Item
        label="Deal Title"
        name="title"
        rules={[{ required: true, min: 5, max: 500, message: 'Title must be at least 5 characters' }]}
      >
        <Input
          placeholder="e.g. Sony WH-1000XM5 Wireless Headphones — 35% off"
          size="large"
          maxLength={500}
          style={{ borderRadius: 8 }}
        />
      </Form.Item>

      <Form.Item label="Description (Optional)" name="description">
        <TextArea
          rows={compact ? 3 : 4}
          placeholder="Provide more details about the deal, key features, or terms…"
          maxLength={5000}
          style={{ borderRadius: 8 }}
        />
      </Form.Item>

      <Form.Item
        label="Deal Image"
        extra="PNG, JPG — max 5MB. The first image becomes the cover."
      >
        <Upload
          listType="picture-card"
          fileList={imageUrl ? [{ uid: 'uploaded', name: 'deal-image', status: 'done', url: imageUrl }] : []}
          beforeUpload={(file) => {
            const isImg = file.type.startsWith('image/');
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isImg) {
              message.error('Only image files are allowed');
              return false;
            }
            if (!isLt5M) {
              message.error('Image must be smaller than 5MB');
              return false;
            }
            uploadImage(file)
              .then((res) => {
                const url = res?.url;
                if (!url) {
                  message.error('Upload succeeded but no image URL was returned');
                  return;
                }
                setImage(url);
                message.success('Image uploaded');
              })
              .catch((err) => {
                message.error(err?.message || 'Upload failed. Check ImageKit configuration.');
              });
            return false;
          }}
          onRemove={() => setImage(null)}
          onPreview={() => {}}
          accept="image/*"
          maxCount={1}
        />
      </Form.Item>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Price"
            name="price"
            rules={[{ required: true, type: 'number', min: 0.01, message: 'Enter a valid price' }]}
          >
            <InputNumber
              prefix={<DollarOutlined style={{ color: '#94a3b8' }} />}
              placeholder="0.00"
              size="large"
              style={{ width: '100%', borderRadius: 8 }}
              min={0.01}
              step={0.01}
              precision={2}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Retail Price (Optional)" name="retailPrice">
            <InputNumber
              prefix={<DollarOutlined style={{ color: '#94a3b8' }} />}
              placeholder="0.00"
              size="large"
              style={{ width: '100%', borderRadius: 8 }}
              min={0.01}
              step={0.01}
              precision={2}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item label="Currency" name="currency" initialValue="USD">
            <Select
              size="large"
              style={{ width: '100%', borderRadius: 8 }}
              options={CURRENCY_OPTIONS}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Condition" name="condition" initialValue="new">
            <Select
              size="large"
              style={{ width: '100%', borderRadius: 8 }}
              options={CONDITION_OPTIONS}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Merchant / Retailer"
            name="merchantId"
            rules={[{ required: true, message: 'Select a merchant' }]}
          >
            <Select
              placeholder="Search or select a merchant"
              showSearch
              size="large"
              style={{ width: '100%', borderRadius: 8 }}
              optionFilterProp="label"
              options={merchantOptions}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item label="Category" name="categoryId">
            <Select
              allowClear
              placeholder="Select a category"
              showSearch
              size="large"
              style={{ width: '100%', borderRadius: 8 }}
              optionFilterProp="label"
              options={categoryOptions}
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Deal URL"
        name="dealUrl"
        rules={[
          { required: true, message: 'Deal URL is required' },
          { type: 'url', message: 'Enter a valid URL' },
          {
            validator: (_, value) => {
              if (!value || String(value).startsWith('https://')) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('URL must use HTTPS'));
            },
          },
        ]}
      >
        <Input placeholder="https://..." size="large" style={{ borderRadius: 8 }} />
      </Form.Item>

      <Form.Item name="isFreeShipping" valuePropName="checked">
        <Checkbox style={{ fontSize: 14 }}>Free shipping</Checkbox>
      </Form.Item>
    </Form>
  );
}

/** Map API deal → form values (dollars, IDs). */
export function dealToFormValues(deal) {
  if (!deal) return {};
  const merchantId = typeof deal.merchant === 'object' && deal.merchant
    ? deal.merchant.id
    : deal.merchantId ?? null;
  const categoryId = typeof deal.category === 'object' && deal.category
    ? deal.category.id
    : deal.categoryId ?? null;

  return {
    title: deal.title,
    description: deal.description || undefined,
    price: deal.priceMinor != null ? deal.priceMinor / 100 : undefined,
    retailPrice: deal.retailPriceMinor != null ? deal.retailPriceMinor / 100 : undefined,
    currency: deal.currency || 'USD',
    condition: deal.condition === 'open_box' ? 'used_open_box' : (deal.condition || 'new'),
    merchantId: merchantId ?? undefined,
    categoryId: categoryId ?? undefined,
    dealUrl: deal.dealUrl,
    isFreeShipping: !!deal.isFreeShipping,
  };
}

/** Map form values → API PATCH/POST body. */
export function formValuesToPayload(values, imageUrl, { clearImagesWhenEmpty = false } = {}) {
  const payload = {
    title: values.title,
    description: values.description || null,
    dealUrl: values.dealUrl,
    priceMinor: Math.round(Number(values.price) * 100),
    retailPriceMinor: values.retailPrice != null
      ? Math.round(Number(values.retailPrice) * 100)
      : null,
    currency: values.currency || 'USD',
    merchantId: values.merchantId ?? null,
    categoryId: values.categoryId ?? null,
    condition: values.condition || 'new',
    isFreeShipping: values.isFreeShipping || false,
  };
  if (imageUrl) {
    payload.imageUrls = [imageUrl];
  } else if (clearImagesWhenEmpty) {
    payload.imageUrls = [];
  }
  return payload;
}
